const API_BASE_URL = '/api';
const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_GET_RETRIES = 2;
const DEFAULT_RETRY_DELAY_MS = 250;
const TRANSIENT_HTTP_STATUSES = new Set([429, 502, 503, 504]);

import { assertVersioned, parseContract } from '../contracts/generated/contracts.js';
import { readLogResponse, saveLogResponse, clearLogResponse } from './responseRevalidation.js';
import { IdentityTransitionError, isIdentityTransitioning } from './identityTransition.js';

function normalizePath(path) {
    if (/^https?:\/\//.test(path)) return path;
    if (path === '/api' || path.startsWith('/api/')) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function createError(message, status, data = null) {
    const error = new Error(message || 'Request failed');
    error.status = status;
    error.data = data;
    error.code = data && typeof data === 'object' ? data.code || null : null;
    error.details = data && typeof data === 'object' ? data.details || {} : {};
    error.requestId = data && typeof data === 'object' ? data.request_id || null : null;
    error.uiState = error.code === 'UNSUPPORTED_API_CONTRACT' ? 'unavailable' : 'runtime_error';
    return error;
}

function csrfToken() {
    if (typeof document === 'undefined') return '';
    const entry = document.cookie.split('; ').find((item) => item.startsWith('csrf_token='));
    return entry ? decodeURIComponent(entry.slice('csrf_token='.length)) : '';
}

function requestSignal(externalSignal, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs);
    if (externalSignal) {
        if (externalSignal.aborted) controller.abort(externalSignal.reason);
        else externalSignal.addEventListener('abort', () => controller.abort(externalSignal.reason), { once: true });
    }
    return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

function safeHttpMessage(status, statusText = '') {
    if (status === 429) return 'Too many requests. Please wait a moment and try again.';
    if ([502, 503, 504].includes(status)) return 'Service is temporarily unavailable. Please try again shortly.';
    return `HTTP ${status}${statusText ? `: ${statusText}` : ''}`;
}

function retryDelay(response, attempt, baseDelayMs) {
    const retryAfterHeader = response?.headers?.get?.('retry-after');
    const retryAfter = retryAfterHeader === null || retryAfterHeader === undefined || retryAfterHeader === ''
        ? Number.NaN
        : Number(retryAfterHeader);
    if (Number.isFinite(retryAfter) && retryAfter >= 0) return Math.min(retryAfter * 1000, 3000);
    return baseDelayMs * (attempt + 1);
}

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

/** Shared transport boundary for API calls. It owns HTTP parsing and errors;
 * feature services own endpoint semantics and response mapping. */
export async function apiRequest(path, options = {}) {
    const {
        raw = false,
        telemetryContext = null,
        timeoutMs = DEFAULT_TIMEOUT_MS,
        requireVersion = true,
        responseContract = null,
        retryCount = null,
        retryBaseDelayMs = DEFAULT_RETRY_DELAY_MS,
        revalidateLog = false,
        identityChange = false,
        ...requestOptions
    } = options;
    if (isIdentityTransitioning() && !identityChange) throw new IdentityTransitionError();
    const method = String(requestOptions.method || 'GET').toUpperCase();
    const idempotent = method === 'GET' || method === 'HEAD';
    const retries = retryCount === null ? (idempotent ? DEFAULT_GET_RETRIES : 0) : Math.max(0, Number(retryCount) || 0);
    const headers = { ...(requestOptions.headers || {}) };
    const cachePath = normalizePath(path);
    let cached = revalidateLog ? readLogResponse(cachePath) : null;
    if (cached) {
        try {
            if (requireVersion) assertVersioned(cached.data);
            if (responseContract) parseContract(responseContract, cached.data);
        } catch { clearLogResponse(); cached = null; }
    }
    if (cached) headers['If-None-Match'] = cached.etag;
    if (revalidateLog) requestOptions.cache = 'no-store';
    if (telemetryContext && typeof telemetryContext === 'object') {
        const contextHeaders = {
            'X-Corecry-UI-Route': telemetryContext.uiRoute,
            'X-Corecry-UI-Component': telemetryContext.component,
            'X-Corecry-Bot-Id': telemetryContext.botId,
            'X-Corecry-Market-Type': telemetryContext.marketType,
            'X-Corecry-Request-Correlation-Id': telemetryContext.requestCorrelationId,
            'X-Corecry-Page-Visibility': telemetryContext.pageVisibility,
            'X-Corecry-Poll-Interval-Ms': telemetryContext.pollIntervalMs,
        };
        for (const [name, value] of Object.entries(contextHeaders)) {
            if (value !== undefined && value !== null && String(value).trim() !== '') {
                headers[name] = String(value).slice(0, 128);
            }
        }
    }
    const hasHeader = (name) => Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());
    if (requestOptions.body && !hasHeader('Content-Type') && !(requestOptions.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        const token = csrfToken();
        if (token) headers['X-CSRF-Token'] = token;
    }
    let response;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
        if (isIdentityTransitioning() && !identityChange) throw new IdentityTransitionError();
        const managedSignal = requestSignal(requestOptions.signal, timeoutMs);
        try {
            response = await fetch(normalizePath(path), {
                credentials: 'include',
                ...requestOptions,
                method,
                headers,
                signal: managedSignal.signal,
            });
        } catch (error) {
            if (managedSignal.signal.aborted && !requestOptions.signal?.aborted) {
                throw createError('Request timed out', 0, { code: 'REQUEST_TIMEOUT' });
            }
            if (!idempotent || attempt >= retries || requestOptions.signal?.aborted) throw error;
        } finally {
            managedSignal.clear();
        }
        if (response && (!TRANSIENT_HTTP_STATUSES.has(response.status) || attempt >= retries)) break;
        await response?.body?.cancel?.();
        await wait(retryDelay(response, attempt, retryBaseDelayMs));
    }
    if (revalidateLog && response.status >= 400) clearLogResponse();
    if (raw) {
        if (!response.ok) {
            throw createError(safeHttpMessage(response.status, response.statusText), response.status);
        }
        return response;
    }
    const contentType = response.headers?.get?.('content-type') || '';
    let data;
    if (response.status === 304 && cached) {
        data = cached.data;
    } else if (typeof response.json === 'function' && (contentType.includes('application/json') || typeof response.text !== 'function')) {
        data = await response.json();
    } else if (typeof response.text === 'function') {
        data = await response.text();
    } else {
        data = { error: `HTTP ${response.status}${response.statusText ? `: ${response.statusText}` : ''}` };
    }
    const hasResponseHeaders = typeof response.headers?.get === 'function';
    const carriesBodyVersion = data && !Array.isArray(data) && typeof data === 'object'
        && ('api_version' in data || 'schema_version' in data);
    const enforceContract = requireVersion && (hasResponseHeaders || carriesBodyVersion);
    if (enforceContract && data && typeof data === 'object') assertVersioned(data, response.headers);
    if (!response.ok && !(response.status === 304 && cached)) {
        if (enforceContract && data && typeof data === 'object') parseContract('api_error', data);
        const message = typeof data === 'object'
            ? data.message || data.error
            : safeHttpMessage(response.status, response.statusText);
        const errorData = data && typeof data === 'object'
            ? data
            : { code: `HTTP_${response.status}` };
        throw createError(message, response.status, errorData);
    }
    if (enforceContract && (!data || typeof data !== 'object')) {
        throw createError('API response is not versioned', response.status, { code: 'MISSING_API_CONTRACT_VERSION' });
    }
    const result = responseContract ? parseContract(responseContract, data) : data;
    if (revalidateLog) saveLogResponse(cachePath, response.headers?.get?.('etag'), result);
    return result;
}

export { API_BASE_URL, createError, normalizePath, safeHttpMessage };
